import { useCallback, useEffect, useSyncExternalStore } from "react";

export type ThemePref = "light" | "dark" | "system";
const KEY = "rp-theme";
const listeners = new Set<() => void>();

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

const systemDark = () => typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;

export function resolvedTheme(pref: ThemePref = readPref()): "light" | "dark" {
  return pref === "system" ? (systemDark() ? "dark" : "light") : pref;
}

function apply() {
  const dark = resolvedTheme() === "dark";
  document.documentElement.classList.toggle("dark", dark);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#0B1220" : "#0F172A");
  listeners.forEach((l) => l());
}

export function setThemePref(pref: ThemePref) {
  try {
    if (pref === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
  } catch {
    /* storage blocked — theme still applies for this page view */
  }
  document.documentElement.classList.add("theme-ready");
  apply();
}

if (typeof window !== "undefined") {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => readPref() === "system" && apply());
  window.addEventListener("storage", (e) => e.key === KEY && apply());
}

/** Current resolved theme ("light" | "dark") + a toggle. Re-renders on change from any tab. */
export function useTheme() {
  const theme = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => resolvedTheme(),
    () => "light" as const,
  );
  useEffect(() => {
    // The pre-paint script in index.html already set the class; keep it in sync after hydration.
    apply();
  }, []);
  const toggle = useCallback(() => setThemePref(resolvedTheme() === "dark" ? "light" : "dark"), []);
  return { theme, toggle, setPref: setThemePref };
}
